// The job cost history lines behind a Labor & Equipment row's JDE hours.
//
// matchJde (billingRecon.js) adds up the cost history lines for one person
// or unit, work date and cost code into a row's JDE St / OT / Total Hours,
// and keeps the lines themselves on the row (jdeLines, and jdePdLines for the
// person-day's per diem). Clicking one of those figures opens this popup: one
// line per cost history entry, with its key, doc type, GL date, cost type,
// hours and cost, so a figure like 20.5 against a 10-hour ticket shows
// whether it is two days' payroll on one date, a reversal and re-post, or a
// second cost code.
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment';
import { docTypeLabel } from './billingCostBasis';

const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};
const r2 = (n) => Math.round(num(n) * 100) / 100;
const fmtH = (n) => r2(n).toLocaleString('en-US', { maximumFractionDigits: 2 });
const fmtMoney = (n) => {
    const v = r2(n);
    const abs = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return v < 0 ? `(${abs})` : abs;
};
const fDate = (d) => (d ? moment.utc(String(d).slice(0, 10), 'YYYY-MM-DD').format('M/D/YY') : '');
// The Form_Job_Cost_History_Key a billing line carries (billingRecon's jdeKeyOf).
const keyOf = (t) => t.jk || (String(t.key || '').startsWith('bl2_') ? String(t.key).slice(4).replace(/#\d+$/, '') : '');

// Who or what a line is for: "Name - 158049" for a person, "Unit - description" for equipment.
export const whoOf = (t) => {
    const sid = String(t.sid || '').trim();
    const who = String(t.who || '').trim();
    if (sid || who) return [who, sid].filter(Boolean).join(' - ');
    const unit = String(t.unit || '').trim();
    const eq = String(t.eq || '').trim();
    // JDE's equipment description often starts with the unit id already ("S12-CC77 - TL-ET-1224").
    if (!eq || eq.toUpperCase() === unit.toUpperCase()) return unit;
    return !unit || eq.toUpperCase().startsWith(unit.toUpperCase()) ? eq : `${unit} - ${eq}`;
};

const CT_LABEL = { 51100: 'Straight time', 51200: 'Overtime', 51500: 'Per diem', 52100: 'Equipment usage' };
export const costTypeLabel = (t) => {
    if (t.rentUsage) return 'Rental usage';
    const ct = String(t.ct || '');
    return `${ct}${CT_LABEL[ct] ? ` ${CT_LABEL[ct]}` : t.ctDesc ? ` ${t.ctDesc}` : ''}`.trim();
};

/**
 * Every cost history line the rows carry, once each (a person row and the
 * rows under it share lines), in work date, GL date, cost type order, with
 * the totals the grid shows: straight time (everything but 51200), overtime
 * (51200), per diem days (51500, a reversal counts -1) and cost.
 */
export const jdeLinesOf = (rows) => {
    const seen = new Set();
    const lines = [];
    (rows || []).forEach((r) => {
        [...(r.jdeLines || []), ...(r.jdePdLines || [])].forEach((t) => {
            const k = t.key || keyOf(t) || `${t.date}|${t.ct}|${t.hrs}|${t.cost}`;
            if (seen.has(k)) return;
            seen.add(k);
            lines.push(t);
        });
    });
    lines.sort((a, b) => String(a.date).localeCompare(String(b.date))
        || whoOf(a).localeCompare(whoOf(b))
        || String(a.gl || '').localeCompare(String(b.gl || ''))
        || String(a.ct || '').localeCompare(String(b.ct || '')));
    const hrsLines = lines.filter((t) => t.ct !== '51500');
    const st = hrsLines.filter((t) => t.ct !== '51200').reduce((a, t) => a + num(t.hrs), 0);
    const ot = hrsLines.filter((t) => t.ct === '51200').reduce((a, t) => a + num(t.hrs), 0);
    const pd = lines.filter((t) => t.ct === '51500').reduce((a, t) => a + (num(t.cost) > 0 ? 1 : num(t.cost) < 0 ? -1 : 0), 0);
    return {
        lines,
        st: r2(st),
        ot: r2(ot),
        hrs: r2(st + ot),
        pd,
        cost: r2(lines.reduce((a, t) => a + num(t.cost), 0)),
        // What tends to explain a JDE figure off the ticket.
        reversals: lines.filter((t) => num(t.hrs) < 0 || num(t.cost) < 0).length,
        glDates: new Set(lines.map((t) => String(t.gl || '').slice(0, 10)).filter(Boolean)).size,
        codes: [...new Set(hrsLines.map((t) => String(t.cc || '').trim()).filter(Boolean))],
    };
};

/** Whether any of the rows has cost history lines to show. */
export const hasJdeLines = (rows) => (rows || []).some((r) => (r.jdeLines || []).length || (r.jdePdLines || []).length);

const th = {
    fontSize: 12, fontWeight: 500, color: 'var(--muted-foreground)', textAlign: 'left', background: 'var(--subtle)',
    borderBottom: '1px solid var(--border)', padding: '8px 10px', whiteSpace: 'nowrap', position: 'sticky', top: 0,
};
const td = { fontSize: 13, padding: '7px 10px', borderBottom: '1px solid var(--border)', verticalAlign: 'top' };
const numTd = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
const stat = { padding: '8px 14px', borderLeft: '1px solid var(--border)' };
const statL = { fontSize: 12, fontWeight: 500, color: 'var(--muted-foreground)' };
const statV = { fontSize: 17, fontWeight: 600, fontVariantNumeric: 'tabular-nums' };

// target: { title, sub, rows, ft } — ft is the field ticket hours the rows
// stand against, when they have a ticket.
const JdeLinesModal = ({ target, onClose }) => {
    useEffect(() => {
        if (!target) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [target, onClose]);
    if (!target) return null;
    const s = jdeLinesOf(target.rows);
    const ft = target.ft == null ? null : r2(target.ft);
    const diff = ft == null ? null : r2(s.hrs - ft);
    const notes = [
        s.reversals ? `${s.reversals} reversing ${s.reversals === 1 ? 'line' : 'lines'} (negative hours or cost)` : '',
        s.glDates > 1 ? `Posted on ${s.glDates} GL dates` : '',
        s.codes.length > 1 ? `Spread over ${s.codes.length} cost codes` : '',
    ].filter(Boolean);
    // On document.body: the billing page sits inside a transformed box, which
    // would pin a fixed overlay to the box instead of the window.
    return createPortal(
        <div
            role="presentation"
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0, zIndex: 60, background: 'rgb(0 0 0 / 0.5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
            }}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-label="JDE cost history lines"
                onClick={(e) => e.stopPropagation()}
                style={{
                    background: 'var(--popover)', color: 'var(--foreground)', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-md)', width: 1040, maxWidth: '96vw',
                    maxHeight: '84vh', display: 'flex', flexDirection: 'column', fontFamily: 'var(--font-body)',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 16, fontWeight: 600 }}>JDE job cost history</div>
                        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
                            {[target.title, target.sub].filter(Boolean).join(' · ')}
                        </div>
                    </div>
                    <button
                        type="button"
                        aria-label="Close"
                        onClick={onClose}
                        className="rc-btn"
                        style={{ width: 32, height: 32, padding: 0, borderRadius: 'var(--radius-pill)', fontSize: 16, lineHeight: '16px' }}
                    >
                        ×
                    </button>
                </div>

                <div style={{ padding: '12px 18px 0' }}>
                    <div style={{ display: 'inline-flex', flexWrap: 'wrap', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--subtle)' }}>
                        <div style={{ ...stat, borderLeft: 0 }}><div style={statL}>St hours</div><div style={statV}>{fmtH(s.st)}</div></div>
                        <div style={stat}><div style={statL}>OT hours</div><div style={statV}>{fmtH(s.ot)}</div></div>
                        <div style={stat}><div style={statL}>JDE total</div><div style={statV}>{fmtH(s.hrs)}</div></div>
                        {ft != null ? <div style={stat}><div style={statL}>Field ticket</div><div style={statV}>{fmtH(ft)}</div></div> : null}
                        {diff ? (
                            <div style={stat}>
                                <div style={statL}>JDE − ticket</div>
                                <div style={{ ...statV, color: 'var(--destructive-text)' }}>{`${diff > 0 ? '+' : ''}${fmtH(diff)}`}</div>
                            </div>
                        ) : null}
                        {s.pd ? <div style={stat}><div style={statL}>Per diem days</div><div style={statV}>{fmtH(s.pd)}</div></div> : null}
                        <div style={stat}><div style={statL}>Cost</div><div style={statV}>{fmtMoney(s.cost)}</div></div>
                    </div>
                    {notes.length ? (
                        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 8 }}>{notes.join(' · ')}</div>
                    ) : null}
                </div>

                <div style={{ overflow: 'auto', margin: '12px 18px 16px', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                    <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
                        <thead>
                            <tr>
                                <th style={th}>Work date</th>
                                <th style={th}>Person / unit</th>
                                <th style={th}>GL date</th>
                                <th style={th}>Doc type</th>
                                <th style={th}>Cost type</th>
                                <th style={th}>Cost code</th>
                                <th style={{ ...th, textAlign: 'right' }}>Hours</th>
                                <th style={{ ...th, textAlign: 'right' }}>Cost</th>
                                <th style={th}>Billing summary</th>
                                <th style={th}>Description</th>
                                <th style={th}>Cost history key</th>
                            </tr>
                        </thead>
                        <tbody>
                            {s.lines.map((t, i) => {
                                const neg = num(t.hrs) < 0 || num(t.cost) < 0;
                                return (
                                    <tr key={t.key || i}>
                                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{fDate(t.date)}</td>
                                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{whoOf(t) || '—'}</td>
                                        <td style={{ ...td, whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>{fDate(t.gl)}</td>
                                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{t.dct ? docTypeLabel(t.dct) : '—'}</td>
                                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{costTypeLabel(t)}</td>
                                        <td style={{ ...td, whiteSpace: 'nowrap' }} title={t.ccDesc || ''}>{t.cc || '—'}</td>
                                        <td style={{ ...numTd, color: neg ? 'var(--destructive)' : undefined }}>{t.ct === '51500' ? '' : fmtH(t.hrs)}</td>
                                        <td style={{ ...numTd, color: neg ? 'var(--destructive)' : undefined }}>{fmtMoney(t.cost)}</td>
                                        <td style={{ ...td, whiteSpace: 'nowrap', color: t.inv ? undefined : 'var(--muted-foreground)' }}>
                                            {t.inv || (t.nb ? 'Not billable' : '—')}
                                        </td>
                                        <td style={{ ...td, color: 'var(--text-secondary)', minWidth: 160 }}>{t.ds || t.ctDesc || '—'}</td>
                                        <td style={{ ...td, fontSize: 12, color: 'var(--text-secondary)', wordBreak: 'break-all', minWidth: 150 }}>{keyOf(t) || '—'}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td style={{ ...td, fontWeight: 600, borderBottom: 0 }} colSpan={6}>{`${s.lines.length} ${s.lines.length === 1 ? 'line' : 'lines'}`}</td>
                                <td style={{ ...numTd, fontWeight: 600, borderBottom: 0 }}>{fmtH(s.hrs)}</td>
                                <td style={{ ...numTd, fontWeight: 600, borderBottom: 0 }}>{fmtMoney(s.cost)}</td>
                                <td style={{ ...td, borderBottom: 0 }} colSpan={3} />
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default JdeLinesModal;
